import React, { useState } from "react";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, IconButton, Button, Alert, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import { useBrandIngredientsQuery, useDeleteBrandIngredient, useUploadBrandIngredients } from "./hooks";

export default function BrandIngredientsSection({ brandId }) {
  const { data: brandIngredients, isLoading: isIngredientsLoading } = useBrandIngredientsQuery(brandId);
  const deleteIngredient = useDeleteBrandIngredient(brandId);
  const uploadIngredients = useUploadBrandIngredients(brandId);

  const [showUploader, setShowUploader] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const handleDeleteIngredient = async (ingredientId) => {
    try {
      await deleteIngredient.mutateAsync(ingredientId);
    } catch (error) {
      console.error("Failed to delete ingredient:", error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        setUploadResult({ type: "error", message: "Please select a CSV file" });
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadResult({ type: "error", message: "Please select a CSV file" });
      return;
    }

    try {
      setUploadResult(null);

      // Read the file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error("Failed to read file"));
        reader.readAsText(selectedFile);
      });

      const response = await uploadIngredients.mutateAsync(fileContent);
      setUploadResult({
        type: "success",
        message: response.message || "Ingredients uploaded successfully!"
      });
      setSelectedFile(null);
      setShowUploader(false);
    } catch (error) {
      setUploadResult({
        type: "error",
        message: error.message || "Failed to upload ingredients"
      });
    }
  };

  return (
    <Box mt={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h3">
          Brand Ingredients
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadIcon />}
          onClick={() => setShowUploader(!showUploader)}
          sx={{ minWidth: "auto" }}
        >
          Upload CSV
        </Button>
      </Box>

      <Collapse in={showUploader}>
        <Box mb={3} p={2} border={1} borderColor="grey.300" borderRadius={1}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Ingredients CSV
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Upload a CSV file with ingredients for this brand. CSV should contain at least an &quot;ingredient_name&quot; column.
            Optionally include &quot;product_name&quot; for product-specific ingredients.
            Multiple ingredients can be comma-separated in a single row.
          </Typography>
          <Box mb={2}>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="csv-file-input"
            />
            <label htmlFor="csv-file-input">
              <Button
                variant="outlined"
                component="span"
                sx={{ mb: 1 }}
              >
                Choose CSV File
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploadIngredients.isPending}
              size="small"
            >
              {uploadIngredients.isPending ? "Uploading..." : "Upload"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setShowUploader(false);
                setSelectedFile(null);
                setUploadResult(null);
              }}
              size="small"
            >
              Cancel
            </Button>
          </Box>
          {uploadResult && (
            <Alert severity={uploadResult.type} sx={{ mt: 2 }}>
              {uploadResult.message}
            </Alert>
          )}
        </Box>
      </Collapse>
      {isIngredientsLoading
        ? (
          <Box mb={2}>
            <Typography>Loading ingredients...</Typography>
          </Box>
        )
        : brandIngredients?.ingredients?.length > 0
          ? (
            <>
              <Typography variant="h6" gutterBottom>
                {brandIngredients.ingredients.length} ingredient{brandIngredients.ingredients.length === 1 ? "" : "s"} found
              </Typography>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    View Ingredients ({brandIngredients.ingredients.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {(() => {
                      const groups = brandIngredients.ingredients.reduce((groups, ingredient) => {
                        const key = ingredient.productName || "General";
                        if (!groups[key]) {
                          groups[key] = [];
                        }
                        groups[key].push(ingredient);
                        return groups;
                      }, {});

                      return Object.entries(groups)
                        .sort(([a], [b]) => {
                          if (a === "General") {
                            return 1;
                          }
                          if (b === "General") {
                            return -1;
                          }
                          return a.localeCompare(b);
                        })
                        .map(([productName, ingredients]) => (
                          <Box key={productName} mb={2}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              {productName}
                            </Typography>
                            <Box sx={{ pl: 2, m: 0 }}>
                              {ingredients
                                .sort((a, b) => a.ingredientName.localeCompare(b.ingredientName))
                                .map((ingredient) => (
                                  <Box key={ingredient.id} display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
                                    <Typography variant="body2">
                                      • {ingredient.ingredientName}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteIngredient(ingredient.id)}
                                      disabled={deleteIngredient.isPending}
                                      sx={{ ml: 1, minWidth: "auto", p: 0.5 }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ))}
                            </Box>
                          </Box>
                        ));
                    })()}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )
          : (
            <Typography variant="body2" color="textSecondary">
              No ingredients found for this brand.
            </Typography>
          )}
    </Box>
  );
}
